// DatesFacet — "Four dates, one open" (owner design, FACET 07B, 2026-09-06).
//
// The four date DIMENSIONS of the search grammar (added to our book · posted on the portal · last verified ·
// last changed) as four rows in ONE frame. Only the engaged dimension is open; the others show their
// provenance hint on the right ("ჩვენი", "კრაულერი", "განცხადება", or the share of adverts with no portal
// date). Opening one collapses the others. The open row is a single segmented control — 7 · 30 · 90 and a
// trailing unit selector whose "სხვა…" IS the custom range — then a dd.mm.yyyy → dd.mm.yyyy pair whose
// open end reads „დღეს" rather than sitting empty (an unbounded range is a stated fact, not a blank).
//
// Deliberately NOT here, per the design: counts on the preset buttons (a figure equal to the corpus total
// discriminates nothing and drags the honest ones down); native US date inputs (dd.mm.yyyy, never mm/dd/yy);
// a card inside a card (the facet rule separates dimensions; nothing else needs a box).
//
// The substitution ("when the portal gives no date, use the day we added it") is a SWITCH, not a tick in a
// paragraph, OFF by default, with one line saying what turning it on changes — it is a different question,
// not a refinement. This reverses the 2026-09-02 "default ON"; the later design ruling wins.
//
// State and semantics are unchanged: `dateRanges` {dim: {from, to}} in ISO, written through the same
// `setDateRange`; the posted dimension rides `posted` or `posted_or_added` exactly as before. This file
// only DRAWS. It mounts in src/harness.jsx outside the login gate so it can be looked at.
import React, { useState } from 'react'

export const DATE_DIMS_ORDER = ['added', 'posted', 'last_seen', 'updated']
const PRESETS = [7, 30, 90]

const isoToDdmmyyyy = (iso) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '')
/** dd.mm.yyyy (also d.m.yyyy, dd/mm/yyyy, dd-mm-yyyy) → ISO, or null when not a date; '' for empty. */
export function ddmmyyyyToIso(text) {
  const s = (text || '').trim()
  if (!s) return ''
  const m = s.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/)
  if (!m) return null
  const d = +m[1], mo = +m[2], y = +m[3]
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null
  const iso = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  const chk = new Date(iso + 'T00:00:00Z')
  return chk.getUTCDate() === d && chk.getUTCMonth() === mo - 1 ? iso : null
}
export const presetFrom = (days, now = Date.now()) => new Date(now - days * 864e5).toISOString().slice(0, 10)

function DateText({ value, placeholder, onCommit, ariaLabel, className }) {
  const [text, setText] = useState(isoToDdmmyyyy(value))
  const [bad, setBad] = useState(false)
  const [lastValue, setLastValue] = useState(value)
  if (value !== lastValue) { setLastValue(value); setText(isoToDdmmyyyy(value)); setBad(false) }   // an outside change (preset, clear) re-seeds the text
  const commit = () => {
    const iso = ddmmyyyyToIso(text)
    if (iso === null) { setBad(true); return }
    setBad(false); onCommit(iso)
  }
  return (
    <input type="text" inputMode="numeric" value={text} placeholder={placeholder} aria-label={ariaLabel} aria-invalid={bad || undefined}
      className={`${className} ${bad ? 'border-rose-500' : ''}`}
      onChange={e => setText(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit() } }} />
  )
}

/**
 * @param {object} p
 * @param {object} p.dateRanges         {dim: {from, to}} (ISO)
 * @param {(dim, patch) => void} p.setDateRange
 * @param {'posted'|'posted_or_added'} p.postedDim   which key the posted dimension currently writes
 * @param {(on: boolean) => void} p.onSubstitute     the switch
 * @param {number|null} [p.postedEmptyPct]           share of adverts with no portal date, for the hint
 * @param {(k: string, vars?: object) => string} p.t
 */
export default function DatesFacet({ dateRanges = {}, setDateRange, postedDim = 'posted', onSubstitute, postedEmptyPct = null, t }) {
  const tr = t || (k => k)
  const rangeOf = (dim) => dateRanges[dim === 'posted' ? postedDim : dim] || { from: '', to: '' }
  const engaged = DATE_DIMS_ORDER.find(d => { const r = rangeOf(d); return r.from || r.to }) || null
  const [openDim, setOpenDim] = useState(engaged)
  const [custom, setCustom] = useState(false)
  const substituteOn = postedDim === 'posted_or_added'
  const hintOf = (dim) => dim === 'posted'
    ? (postedEmptyPct != null ? tr('desk_dates_hint_empty_pct', { pct: postedEmptyPct }) : tr('desk_dates_hint_portal'))
    : tr('desk_dates_hint_' + dim)
  const key = (dim) => (dim === 'posted' ? postedDim : dim)

  return (
    <div className="text-sm">
      {DATE_DIMS_ORDER.map(dim => {
        const r = rangeOf(dim)
        const on = !!(r.from || r.to)
        const open = openDim === dim
        const picked = PRESETS.find(n => r.from === presetFrom(n) && !r.to) || null
        return (
          <div key={dim} className="border-b border-border/40 last:border-b-0">
            {/* the row head: name left, hint (or the value when engaged and closed) right; click opens ONE */}
            <button type="button" onClick={() => setOpenDim(open ? null : dim)} aria-expanded={open}
              className="flex w-full items-center gap-2 py-1.5 text-left hover:text-foreground">
              <span className="w-3 shrink-0 text-xs text-muted-foreground">{open ? '−' : '+'}</span>
              <span className={`flex-1 truncate ${on ? 'font-medium text-foreground' : 'text-foreground/90'}`}>{tr('desk_filter_date_' + dim)}</span>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {on && !open
                  ? (picked ? tr('desk_dates_last_n', { n: picked }) : `${isoToDdmmyyyy(r.from) || '…'} → ${isoToDdmmyyyy(r.to) || tr('desk_dates_today')}`)
                  : hintOf(dim)}
              </span>
            </button>
            {open && (
              <div className="pb-2 pl-5 space-y-1.5">
                {/* one segment: numerals only, the unit in the trailing selector; „სხვა…" lives there, not as a fourth button */}
                <div className="flex rounded-md border border-border overflow-hidden text-xs">
                  {PRESETS.map(n => (
                    <button key={n} type="button" aria-pressed={picked === n}
                      className={`flex-1 py-1 tabular-nums border-r border-border ${picked === n ? 'bg-foreground text-background font-medium' : 'hover:bg-muted'}`}
                      onClick={() => { setCustom(false); setDateRange(key(dim), picked === n ? { from: '', to: '' } : { from: presetFrom(n), to: '' }) }}>
                      {n}
                    </button>
                  ))}
                  <select value={custom || (!picked && on) ? 'custom' : 'days'} aria-label={tr('desk_dates_unit')}
                    className="flex-1 bg-transparent py-1 pl-2 pr-1 text-xs"
                    onChange={e => { const c = e.target.value === 'custom'; setCustom(c); if (c && picked) setDateRange(key(dim), { from: '', to: '' }) }}>
                    <option value="days">{tr('desk_dates_unit_days')}</option>
                    <option value="custom">{tr('desk_dates_unit_custom')}</option>
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <DateText value={r.from} placeholder="dd.mm.yyyy" ariaLabel={tr('desk_filter_date_from')}
                    className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs tabular-nums"
                    onCommit={iso => setDateRange(key(dim), { from: iso })} />
                  <span className="text-xs text-muted-foreground">→</span>
                  <DateText value={r.to} placeholder={tr('desk_dates_today')} ariaLabel={tr('desk_filter_date_to')}
                    className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 text-xs tabular-nums"
                    onCommit={iso => setDateRange(key(dim), { to: iso })} />
                  {on && (
                    <button type="button" className="shrink-0 px-1 text-xs text-muted-foreground hover:text-foreground" title={tr('filter_clear')}
                      onClick={() => { setCustom(false); setDateRange(key(dim), { from: '', to: '' }) }}>×</button>
                  )}
                </div>
              </div>
            )}
          </div>
        )
      })}
      {/* the substitution: a switch, not prose. Off by default; one line says what turning it on changes. */}
      <div className="mt-2 border-t border-dashed border-border/60 pt-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <button type="button" role="switch" aria-checked={substituteOn} onClick={() => onSubstitute(!substituteOn)}
            className={`relative h-4 w-8 shrink-0 rounded-full transition-colors ${substituteOn ? 'bg-foreground' : 'bg-muted-foreground/40'}`}>
            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-background transition-transform ${substituteOn ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </button>
          <span className="text-sm">{tr('desk_dates_substitute')}</span>
        </label>
        <p className="mt-1 pl-10 text-xs text-muted-foreground">{substituteOn ? tr('desk_dates_substitute_on') : tr('desk_dates_substitute_off')}</p>
      </div>
    </div>
  )
}
